/**
 * Type definitions for the new Packet API (V2)
 * These types align with the backend's scalable packet generation system
 */

import { DraftType } from "./draft";

/**
 * Status enum for packet generation lifecycle
 */
export enum PacketGenerationStatus {
  NOT_STARTED = "Not Started",
  IN_PROGRESS = "In Progress",
  GENERATING = "Generating",
  GENERATED = "Generated",
  NEEDS_UPDATE = "Needs Update",
  FAILED = "Failed"
}

/**
 * Pagination position options for packet pages
 */
export type PaginationPosition =
  | "bottomRight"
  | "bottomLeft"
  | "bottomCenter"
  | "topRight"
  | "topLeft"
  | "topCenter";

/**
 * Options for when pagination should start
 */
export type PaginationStartOption = "firstPage" | "afterExhibitList";
export type DividerTabTextSize = "small" | "normal" | "large";

/**
 * Configuration for packet dividers/separators
 */
export interface PacketDividerConfig {
  /** Whether dividers are enabled at all */
  enabled: boolean;
  /** Show dividers between main document groups */
  mainGroupEnabled: boolean;
  /** Show dividers between categories within groups */
  categoryEnabled: boolean;
  /** Show dividers between individual documents */
  documentEnabled: boolean;
  /** Color of the dividers (hex code) */
  color: string;
  /** Text size for divider titles */
  textSize: DividerTabTextSize;
}

/**
 * Configuration for packet pagination
 */
export interface PacketPaginationConfig {
  /** Whether pagination is enabled */
  enabled: boolean;
  /** Position of page numbers */
  position: PaginationPosition;
  /** When to start numbering pages */
  startOn: PaginationStartOption;
  /** Whether to include page numbers on dividers */
  includePageNumbersOnDividers: boolean;
}

/**
 * Enhanced configuration options for packet generation
 */
export interface PacketConfig {
  /** Optional custom name for the packet */
  packetName?: string;
  /** Divider/separator configuration */
  divider: PacketDividerConfig;
  /** Pagination configuration */
  pagination: PacketPaginationConfig;
  /** Which draft types to include in the packet */
  includeDrafts?: DraftType[];
  /** Which documents to include (by document IDs) */
  includeDocuments?: string[];
  /** Custom ordering of packet sections */
  sectionOrder?: string[];
  /** Whether to include cover letter */
  includeCoverLetter?: boolean;
  /** Whether to include exhibit list */
  includeExhibitList?: boolean;
  /** Additional custom options */
  options?: Record<string, any>;
}

/**
 * Information about a document included in the packet
 */
export interface PacketDocument {
  /** Document ID */
  document_id: string;
  /** Document title/name */
  title: string;
  /** Document type/category */
  type: string;
  /** Order/position in the packet */
  order: number;
  /** Number of pages in this document */
  page_count?: number;
  /** Whether this document is included */
  included: boolean;
}

/**
 * Information about a draft included in the packet
 */
export interface PacketDraft {
  /** Draft ID */
  draft_id: string;
  /** Type of draft */
  draft_type: DraftType;
  /** Draft title */
  title?: string;
  /** Order/position in the packet */
  order: number;
  /** Number of pages in this draft */
  page_count?: number;
  /** Whether this draft is included */
  included: boolean;
}

/**
 * The packet's saved build options, persisted on the backend so the attorney's
 * choices survive every rebuild (UI or agent). Partial patches merge per
 * section.
 */
export interface PacketSettings {
  packet_name?: string | null;
  divider: Omit<PacketDividerConfig, "enabled">;
  pagination: PacketPaginationConfig;
}

export type PacketSettingsPatch = {
  packet_name?: string | null;
  divider?: Partial<Omit<PacketDividerConfig, "enabled">>;
  pagination?: Partial<PacketPaginationConfig>;
};

/**
 * Main Packet entity representing a generated document packet
 */
export interface Packet {
  /** Unique identifier for the packet */
  packet_id: string;
  /** ID of the individual/beneficiary this packet belongs to */
  individual_id: string;
  /** Current generation status */
  status: PacketGenerationStatus;
  /** Flag indicating the packet is stale vs underlying documents/drafts and should be regenerated */
  needs_update?: boolean;
  /** Saved build options; the next build uses these unless overridden */
  settings?: PacketSettings;
  /** Set when a PDF edit replaced the built file; a rebuild discards it */
  last_manual_edit?: { at?: string; by?: string; note?: string } | null;
  /** Configuration used for this packet */
  config: PacketConfig;
  /** List of documents included in the packet */
  documents: PacketDocument[];
  /** List of drafts included in the packet */
  drafts: PacketDraft[];
  /** file Path */
  file_path?: string;
  /** GCS path to the divider-pages-only PDF, if generated */
  divider_file_path?: string;
  /** GCS path to the divider-tabs-only Word (.docx) file, if generated */
  divider_word_file_path?: string;
  /** URL to download the generated PDF (if available) */
  pdf_url?: string;
  /** GCS path to the generated PDF */
  pdf_path?: string;
  /** Total number of pages in the packet */
  total_pages?: number;
  /** Size of the generated PDF in bytes */
  file_size?: number;
  /** Timestamp when packet was created */
  created_at?: string;
  /** Timestamp when packet was last updated */
  updated_at?: string;
  /** Timestamp when generation was completed */
  generated_at?: string;
  /** Optional generation call ID for tracking */
  generate_call_id?: string;
  /** Error message if generation failed */
  error_message?: string;
  /**
   * What the last build printed ahead of the exhibits: drafts and forms
   * (`form_id`), or why it couldn't. Null when it had no front matter.
   */
  front_matter_report?: {
    parts?: {
      draft_id?: string;
      form_id?: string;
      title?: string;
      pages?: number;
    }[];
    error?: string;
  } | null;
}

/**
 * Backend request payload for generating a packet
 * This matches the actual backend API structure
 */
export interface GeneratePacketRequest {
  /** ID of the individual/beneficiary */
  individual_id: string;
  /** Whether to include the legal brief/cover letter content */
  include_brief?: boolean;
  /** Whether to include the exhibit list */
  include_exhibit_list?: boolean;
  /** Whether to include evidence documents */
  include_evidence?: boolean;
  /** Optional custom name for the packet */
  packet_name?: string;
  /** Divider configuration */
  divider?: PacketDividerConfig;
  /** Pagination configuration */
  pagination?: PacketPaginationConfig;
  /** Optional generation call ID for tracking */
  generate_call_id?: string;
}

/**
 * Frontend request payload for generating a packet by individual
 * (convenience method that uses PacketConfig structure)
 */
export interface GeneratePacketByIndividualRequest {
  /** Optional custom packet configuration */
  config?: Partial<PacketConfig>;
}

/**
 * Response from packet generate endpoint (synchronous)
 */
export interface GeneratePacketResponse {
  /** The generated packet */
  packet: Packet;
  /** Unique ID for tracking this generation call */
  generate_call_id: string;
}

/**
 * Response from packet generate-async endpoint
 */
export interface GeneratePacketAsyncResponse {
  /** Success message */
  message: string;
  /** Unique ID for tracking this async generation call */
  generate_call_id: string;
  /** The packet ID that's being generated */
  packet_id: string;
}

/**
 * Default packet configuration
 */
export const defaultPacketConfig: PacketConfig = {
  packetName: "Case Packet",
  divider: {
    enabled: true,
    mainGroupEnabled: true,
    categoryEnabled: true,
    documentEnabled: false,
    color: "#FFFFFF",
    textSize: "normal"
  },
  pagination: {
    enabled: true,
    position: "bottomRight",
    startOn: "firstPage",
    includePageNumbersOnDividers: false
  }
};

/**
 * Helper type for status colors (for UI badges)
 */
export const PacketStatusColors: Record<PacketGenerationStatus, string> = {
  [PacketGenerationStatus.NOT_STARTED]: "gray",
  [PacketGenerationStatus.IN_PROGRESS]: "orange",
  [PacketGenerationStatus.GENERATING]: "blue",
  [PacketGenerationStatus.GENERATED]: "green",
  [PacketGenerationStatus.NEEDS_UPDATE]: "orange",
  [PacketGenerationStatus.FAILED]: "red"
};

/**
 * Pagination position display labels
 */
export const paginationPositionLabels: Record<PaginationPosition, string> = {
  bottomRight: "Bottom Right",
  bottomLeft: "Bottom Left",
  bottomCenter: "Bottom Center",
  topRight: "Top Right",
  topLeft: "Top Left",
  topCenter: "Top Center"
};

/**
 * Pagination start option display labels
 */
export const paginationStartLabels: Record<PaginationStartOption, string> = {
  firstPage: "First Page",
  afterExhibitList: "After Exhibit List"
};
